/**
 * Customisation: ContentMetadataTags
 *
 * Drop into:
 *   src/customizations/volto/components/theme/ContentMetadataTags/ContentMetadataTags.jsx
 *
 * Adds an og:image fallback chain:
 *   1. lead_image
 *   2. preview_image
 *   3. preview_image_link (linked image relation)
 *   4. image (generic — e.g. Image content type)
 *   5. Site-specific OG fallback image (raster only — SVG not supported by social crawlers)
 *
 * Images are validated against OG_IMAGE_MIN_WIDTH / OG_IMAGE_MIN_HEIGHT before
 * use. A console.warn fires only when something was expected but couldn't be
 * resolved — not for content that simply has no images.
 */

import React from "react";
import { Helmet } from "@plone/volto/helpers";
import { flattenToAppURL, toBackendLang } from "@plone/volto/helpers";
import config from "@plone/volto/registry";

// Minimum dimensions for og:image to be picked up by social platforms.
// Facebook requires 200×200; 600×315 is the practical floor for decent previews.
const OG_IMAGE_MIN_WIDTH = 600;
const OG_IMAGE_MIN_HEIGHT = 315;

// ---------------------------------------------------------------------------
// SITE FALLBACK IMAGE
// Used when no content-level image is found. Must be a raster image (PNG/JPG)
// — SVG is not supported by social crawlers (Facebook, X/Twitter, LinkedIn).
// LinkedIn prefers JPEG over PNG; 1200×630px is the recommended size.
// Update this URL when the site logo or brand image changes.
// ---------------------------------------------------------------------------
const OG_FALLBACK_IMAGE =
  "https://developspace.africa/siteworkspace/fsda-social-logo.png/@@images/image/large";

function getOgImageUrl(scalesObj, downloadUrl) {
  if (!scalesObj && !downloadUrl) return null;

  const preferredOrder = ["large", "great", "huge", "preview"];

  for (const scaleName of preferredOrder) {
    const scale = scalesObj?.[scaleName];
    if (
      scale?.download &&
      scale.width >= OG_IMAGE_MIN_WIDTH &&
      scale.height >= OG_IMAGE_MIN_HEIGHT
    ) {
      return flattenToAppURL(scale.download);
    }
  }

  if (scalesObj) {
    const qualifying = Object.values(scalesObj).find(
      (s) =>
        s?.download &&
        s.width >= OG_IMAGE_MIN_WIDTH &&
        s.height >= OG_IMAGE_MIN_HEIGHT,
    );
    if (qualifying) return flattenToAppURL(qualifying.download);
  }

  if (scalesObj) {
    console.warn(
      "[ContentMetadataTags] Image found but no scale meets og:image minimums " +
        `(${OG_IMAGE_MIN_WIDTH}×${OG_IMAGE_MIN_HEIGHT}px). Scales available:`,
      Object.entries(scalesObj).map(
        ([k, v]) => `${k}: ${v?.width}×${v?.height}`,
      ),
    );
  }

  if (downloadUrl) return flattenToAppURL(downloadUrl);

  return null;
}

function resolveOgImage(content) {
  // 1. Lead image
  if (content.lead_image?.scales) {
    const url = getOgImageUrl(
      content.lead_image.scales,
      content.lead_image.download,
    );
    if (url) return url;
  }

  // 2. Preview image (inline)
  if (content.preview_image?.scales) {
    const url = getOgImageUrl(
      content.preview_image.scales,
      content.preview_image.download,
    );
    if (url) return url;
  }

  // 3. Preview image link (linked image relation).
  if (content.preview_image_link) {
    if (content.preview_image_link.scales) {
      const url = getOgImageUrl(
        content.preview_image_link.scales,
        content.preview_image_link.download,
      );
      if (url) return url;
    } else if (content.preview_image_link["@id"]) {
      // Relation present but not expanded — construct a best-guess URL.
      return flattenToAppURL(
        `${content.preview_image_link["@id"]}/@@images/image/large`,
      );
    } else {
      console.warn(
        "[ContentMetadataTags] preview_image_link is present but has neither " +
          "scales nor @id — cannot resolve og:image from it.",
        content.preview_image_link,
      );
    }
  }

  // 4. Generic image field
  if (content.image?.scales) {
    const url = getOgImageUrl(content.image.scales, content.image.download);
    if (url) return url;
  }

  // 5. Site-wide fallback — only reached when no content image was found.
  // To update, change OG_FALLBACK_IMAGE at the top of this file.
  return OG_FALLBACK_IMAGE;
}

const ContentMetadataTags = (props) => {
  const { content } = props;
  const { settings } = config;

  const ogImage = resolveOgImage(content);
  const ogUrl = `${settings.publicURL}${content["@id"] ? flattenToAppURL(content["@id"]) : ""}`;
  const lang = toBackendLang(
    content.language?.token || settings.defaultLanguage,
  );

  return (
    <Helmet>
      <html lang={lang} />

      {content.title && <title>{content.title}</title>}
      {content.description && (
        <meta name="description" content={content.description} />
      )}

      {content.title && <meta property="og:title" content={content.title} />}
      {content.description && (
        <meta property="og:description" content={content.description} />
      )}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={ogUrl} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      <meta name="twitter:card" content="summary_large_image" />
      {content.title && <meta name="twitter:title" content={content.title} />}
      {content.description && (
        <meta name="twitter:description" content={content.description} />
      )}
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {content.exclude_from_nav && (
        <meta name="robots" content="noindex, follow" />
      )}
    </Helmet>
  );
};

export default ContentMetadataTags;
